import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api/client.js';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { buildPostingPriceLine, countConnectedSocials, formatPostingQuote, parsePostingPeriodEnd } from '../../lib/postingPlan.js';
import { track } from '../../lib/funnel.js';

export default function PostingSubscriptionCard({ entitlements }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dash = useDashboardData() || {};
  const [quote, setQuote] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState(false);

  useEffect(() => {
    if (entitlements?.plan !== 'posting') return undefined;
    try { track('posting_billing_card_shown'); } catch (_) { /* fail-silent */ }
    let cancelled = false;
    api.billing.getPostingQuote(1).then((response) => {
      if (!cancelled) setQuote(formatPostingQuote(response, i18n.language || 'en'));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [entitlements?.plan, i18n.language]);

  if (entitlements?.plan !== 'posting') return null;

  const connected = countConnectedSocials(dash.socialAccounts);
  const quotedQuantity = Number(quote?.currentQuantity);
  const quantity = Number.isFinite(quotedQuantity) && quotedQuantity >= 1
    ? Math.floor(quotedQuantity)
    : Math.max(1, connected);
  const line = buildPostingPriceLine({
    quantity,
    unitPriceCents: quote?.unitAmount,
    currency: quote?.currency || 'EUR',
  });
  // TASK-327: entitlements.nextInvoiceAt/currentPeriodEnd are already ISO
  // strings from D1 — `new Date(isoString)` is safe. quote.periodEnd is NOT:
  // it's a raw Unix-SECONDS number when it comes from Stripe's live invoice
  // preview (see parsePostingPeriodEnd's doc comment in lib/postingPlan.js),
  // and `new Date(secondsNumber)` silently misreads it as MILLISECONDS,
  // producing a date near 1970. Route it through the same parser
  // formatPostingQuote already uses internally instead of a naive `new Date`.
  const nextInvoiceFromEntitlements = entitlements?.nextInvoiceAt || entitlements?.currentPeriodEnd || null;
  const entitlementsDate = nextInvoiceFromEntitlements && !Number.isNaN(new Date(nextInvoiceFromEntitlements).getTime())
    ? new Date(nextInvoiceFromEntitlements)
    : null;
  const { ok: quotePeriodOk, date: quotePeriodDate } = parsePostingPeriodEnd(quote?.periodEnd ?? null);
  const nextInvoiceDate = entitlementsDate || (quotePeriodOk ? quotePeriodDate : null);
  const nextInvoice = nextInvoiceDate
    ? new Intl.DateTimeFormat(i18n.language || 'en', { dateStyle: 'medium' }).format(nextInvoiceDate)
    : null;

  async function manageBilling() {
    if (portalBusy) return;
    setPortalBusy(true);
    setPortalError(false);
    try {
      const result = await api.billing.createPortalSession({ returnUrl: window.location.href });
      const url = result?.portalUrl || result?.data?.portalUrl;
      if (!url) throw new Error('missing_portal_url');
      window.location.assign(url);
    } catch (_) {
      setPortalError(true);
      setPortalBusy(false);
    }
  }

  return (
    <section className="s-card">
      <div className="s-card-head"><h3>{t('postingPlan.currentTitle')}</h3></div>
      <div className="s-card-body">
        <div className="s-notice" role="status">
          <strong>{t('postingPlan.calculation', { unit: line.unitFormatted, quantity, total: line.totalFormatted })}</strong>
        </div>
        {connected === 0 && <p className="muted" style={{ fontSize: 13 }}>{t('postingPlan.minimumQuantity')}</p>}
        {entitlements?.accessState === 'grace'
          ? entitlements.graceEndsAt && <p className="muted">{t('grace.accessUntil')} · {new Date(entitlements.graceEndsAt).toLocaleDateString(i18n.language, { dateStyle: 'medium' })}</p>
          : nextInvoice && <p className="muted">{t('postingPlan.nextInvoice', { date: nextInvoice })}</p>}
        {portalError && <p role="alert">{t('postingBillingClarity.portalError')}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <button type="button" className="btn btn-violet btn-sm" onClick={manageBilling} disabled={portalBusy}>
            {portalBusy ? t('postingPlan.loading') : t('postingBillingClarity.manageBilling')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/app/plans')}>
            {t('settings.changePlan')}
          </button>
        </div>
      </div>
    </section>
  );
}
